import { SHARED_MODE, SPECIAL_MODE } from './enums';

const SYSTEM_FLAGS_ACTION = {
  PRESOAK_START: 'PRESOAK_START',
  PRESOAK_STOP: 'PRESOAK_STOP',
  ACTIVATION_START: 'ACTIVATION_START',
  COLLABORATION_STOP: 'COLLABORATION_STOP',
  RESET: 'RESET',
  DERIVE_FROM_API: 'DERIVE_FROM_API',
};

// Shared handler for active sharedModes (ON, PRIMING) when specialMode is NONE.
// Both produce the same flag state; a named reference makes the identity explicit.
const toActive = state => ({ ...state, isActive: true, isPresoaking: false, isCollaborative: false });

// Shared handler for all collaborative-entry sharedModes (ACTIVATING, PRIMING, ON).
// All three produce the same flag state; a named reference makes the identity explicit.
const toCollaborative = state => ({ ...state, isCollaborative: true, isPresoaking: false, isActive: false });

// Two-level O(1) lookup: specialMode → sharedMode → state transform.
// specialMode partitions the three mutually exclusive activation paths;
// sharedMode refines within each partition.
// DEFAULT handles sharedModes not explicitly listed (e.g., PRESOAKING for any sharedMode,
// or collaborative exit via DEACTIVATING).
const TRANSITION_MAP = {
  [SPECIAL_MODE.PRESOAKING]: {
    DEFAULT: state => ({ ...state, isPresoaking: true, isActive: false, isCollaborative: false }),
  },
  [SPECIAL_MODE.COLLABORATIVE_SOAK]: {
    [SHARED_MODE.ACTIVATING]: toCollaborative,
    [SHARED_MODE.PRIMING]:    toCollaborative,
    [SHARED_MODE.ON]:         toCollaborative,
    DEFAULT:                  state => ({ ...state, isCollaborative: false }),
  },
  [SPECIAL_MODE.NON_EMERGENCY_COOLDOWN]: {
    DEFAULT: state => ({ ...state, isActive: false, isPresoaking: false, isCollaborative: false }),
  },
  [SPECIAL_MODE.NONE]: {
    [SHARED_MODE.ON]:      toActive,
    [SHARED_MODE.PRIMING]: toActive,
    [SHARED_MODE.OFF]: state => ({ ...state, isActive: false, isPresoaking: false, isCollaborative: false }),
    DEFAULT:           state => ({ ...state, isCollaborative: false }),
  },
};

/**
 * Derives system activation flags from an API payload.
 * @param {Object} state - Current flags: { isPresoaking, isActive, isCollaborative }
 * @param {Object} data - API response payload containing systemStatus
 * @returns {Object} New flags state
 */
function deriveFromApiPayload(state, data) {
  if (!data?.systemStatus) return state;

  const { specialMode, sharedMode } = data.systemStatus;
  // First-level lookup: find the partition for this specialMode.
  const modeEntry = TRANSITION_MAP[specialMode];
  // Second-level lookup: prefer an exact sharedMode match; fall back to DEFAULT.
  // An unrecognized specialMode (no modeEntry) is a no-op — state returned unchanged below.
  const handler = modeEntry?.[sharedMode] ?? modeEntry?.DEFAULT;
  if (!handler) return state;

  // Apply the transform and return the original reference if no flag changed,
  // avoiding unnecessary re-renders from useReducer.
  const next = handler(state);
  return (next.isPresoaking    === state.isPresoaking &&
          next.isActive        === state.isActive &&
          next.isCollaborative === state.isCollaborative)
    ? state : next;
}

/**
 * Pure reducer for system activation flags. Intended for use with React's useReducer hook.
 *
 * React owns every invocation of this function — callers never call it directly. When
 * dispatch(action) is called, React invokes deriveSystemState(committedState, action) where
 * committedState is the post-render value held internally by the hook, not a closure snapshot.
 * This guarantees that state is always fresh at the time the reducer runs, eliminating the
 * stale-closure bug that affects useState callbacks registered once and never re-registered.
 *
 * @param {Object} state - Current flags: { isPresoaking, isActive, isCollaborative }
 * @param {Object} action - Action with type and optional payload
 * @returns {Object} New flags state
 */
function deriveSystemState(state, action) {
  switch (action.type) {
    case SYSTEM_FLAGS_ACTION.PRESOAK_START:
      return { ...state, isPresoaking: true };

    case SYSTEM_FLAGS_ACTION.PRESOAK_STOP:
      return { ...state, isPresoaking: false };

    case SYSTEM_FLAGS_ACTION.ACTIVATION_START:
      return { ...state, isPresoaking: false, isCollaborative: false };

    case SYSTEM_FLAGS_ACTION.COLLABORATION_STOP:
      return { ...state, isCollaborative: false };

    case SYSTEM_FLAGS_ACTION.RESET:
      return { isPresoaking: false, isActive: false, isCollaborative: false };

    case SYSTEM_FLAGS_ACTION.DERIVE_FROM_API:
      return deriveFromApiPayload(state, action.payload);

    default:
      return state;
  }
}

export { SYSTEM_FLAGS_ACTION, deriveSystemState, deriveFromApiPayload };
